import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  formatRentalPeriod,
  RENTAL_DAY_OPTIONS,
  RENTAL_HOUR_OPTIONS,
} from '@/feature/device/utils/deviceLabels';
import { organizationService } from '@/feature/organization/services/organizationService';
import { testAccuracyService } from '@/feature/testAccuracy/services/testAccuracyService';
import FormSelect from '@/shared/components/forms/FormSelect';
import FormTextArea from '@/shared/components/forms/FormTextArea';
import Button from '@/shared/components/ui/Button';
import Modal from '@/shared/components/ui/Modals/Modal';

interface IAssignDeviceModalProps {
  isOpen: boolean;
  deviceName: string;
  onClose: () => void;
  onAssign: (data: {
    org_id: string;
    project_id: string;
    rental_days: number;
    rental_hours: number;
    notes?: string;
  }) => Promise<boolean>;
}

interface IAssignForm {
  org_id: string;
  project_id: string;
  rental_days: string;
  rental_hours: string;
  notes: string;
}

const compactField = '!rounded-md !py-2 !text-sm border-gray-300';

const formatProjectLabel = (name: string, code?: string) => (code ? `${name} (${code})` : name);

const getProjectPlaceholder = (
  orgId: string,
  loadingProjects: boolean,
  projectCount: number
): string => {
  if (!orgId) {
    return 'Select organization first';
  }
  if (loadingProjects) {
    return 'Loading projects…';
  }
  if (projectCount) {
    return 'Select project';
  }
  return 'No projects available';
};

const AssignDeviceModal = ({ isOpen, deviceName, onClose, onAssign }: IAssignDeviceModalProps) => {
  const [orgOptions, setOrgOptions] = useState<{ value: string; label: string }[]>([]);
  const [projectOptions, setProjectOptions] = useState<{ value: string; label: string }[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [durationError, setDurationError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<IAssignForm>({
    defaultValues: {
      org_id: '',
      project_id: '',
      rental_days: '1',
      rental_hours: '0',
      notes: '',
    },
  });

  const orgId = watch('org_id');
  const projectId = watch('project_id');
  const rentalDays = watch('rental_days');
  const rentalHours = watch('rental_hours');

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    reset({
      org_id: '',
      project_id: '',
      rental_days: '1',
      rental_hours: '0',
      notes: '',
    });
    setProjectOptions([]);
    setDurationError(null);
    const loadOrgs = async () => {
      const orgs = await organizationService.getOrganizations();
      setOrgOptions(
        orgs.map((org) => ({
          value: String(org.org_id || org.id),
          label: String(org.org_name || org.organizationName || org.name || org.id),
        }))
      );
    };
    void loadOrgs();
  }, [isOpen, reset]);

  useEffect(() => {
    if (!orgId) {
      setProjectOptions([]);
      setValue('project_id', '');
      return;
    }

    let cancelled = false;
    const loadProjects = async () => {
      setLoadingProjects(true);
      const data = await testAccuracyService.getProjects(orgId);
      if (cancelled) {
        return;
      }
      setProjectOptions(
        (data.projects || []).map((project) => ({
          value: project.project_id,
          label: formatProjectLabel(project.project_name, project.code),
        }))
      );
      setLoadingProjects(false);
    };
    void loadProjects();

    return () => {
      cancelled = true;
    };
  }, [orgId, setValue]);

  const handleOrgChange = (value: string | string[]) => {
    const nextOrgId = Array.isArray(value) ? value[0] : value;
    setValue('org_id', String(nextOrgId), { shouldValidate: true });
    setValue('project_id', '', { shouldValidate: true });
  };

  const selectedOrg = orgOptions.find((org) => org.value === orgId);
  const selectedProject = projectOptions.find((project) => project.value === projectId);
  const daysNum = Number(rentalDays || 0);
  const hoursNum = Number(rentalHours || 0);

  const onValid = async (data: IAssignForm) => {
    const days = Number(data.rental_days);
    const hours = Number(data.rental_hours);
    if (days <= 0 && hours <= 0) {
      setDurationError('Select at least 1 day or 1 hour.');
      return;
    }
    setDurationError(null);
    const ok = await onAssign({
      org_id: data.org_id,
      project_id: data.project_id,
      rental_days: days,
      rental_hours: hours,
      notes: data.notes.trim() || undefined,
    });
    if (ok) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title='Assign Device' size='md'>
      <form onSubmit={handleSubmit(onValid)} className='p-6'>
        <p className='text-sm text-gray-500 mb-4'>Rent this device to an organization.</p>
        <div className='space-y-4'>
          <div>
            <span className='text-sm font-medium text-gray-700'>Device</span>
            <p className='mt-1 text-sm font-semibold text-gray-900'>{deviceName}</p>
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <FormSelect
              name='org_id'
              label='Organization'
              options={orgOptions}
              register={register}
              errors={errors}
              value={orgId}
              onChange={handleOrgChange}
              validation={{ required: 'Organization is required' }}
              searchable
              className={compactField}
            />
            <FormSelect
              name='project_id'
              label='Project'
              options={projectOptions}
              register={register}
              errors={errors}
              value={projectId}
              onChange={(value) => setValue('project_id', String(value), { shouldValidate: true })}
              validation={{ required: 'Project is required' }}
              searchable
              disabled={!orgId || loadingProjects}
              placeholder={getProjectPlaceholder(orgId, loadingProjects, projectOptions.length)}
              className={compactField}
            />
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <FormSelect
              name='rental_days'
              label='Days'
              options={RENTAL_DAY_OPTIONS}
              register={register}
              errors={errors}
              value={rentalDays}
              onChange={(value) => {
                setDurationError(null);
                setValue('rental_days', String(value), { shouldValidate: true });
              }}
              validation={{ required: 'Days is required' }}
              className={compactField}
            />
            <FormSelect
              name='rental_hours'
              label='Hours'
              options={RENTAL_HOUR_OPTIONS}
              register={register}
              errors={errors}
              value={rentalHours}
              onChange={(value) => {
                setDurationError(null);
                setValue('rental_hours', String(value), { shouldValidate: true });
              }}
              validation={{ required: 'Hours is required' }}
              className={compactField}
            />
          </div>
          {durationError ? <p className='text-sm text-red-600'>{durationError}</p> : null}
          <FormTextArea
            name='notes'
            label='Notes'
            placeholder='Optional notes'
            register={register}
            errors={errors}
            showRequiredMark={false}
            rows={3}
            resize='vertical'
            preventEmptySpaces={false}
            className={compactField}
          />
        </div>
        <div className='mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm'>
          <div className='flex justify-between py-1'>
            <span className='text-gray-500'>Device</span>
            <span className='font-semibold text-gray-900'>{deviceName}</span>
          </div>
          <div className='flex justify-between py-1'>
            <span className='text-gray-500'>Organization</span>
            <span className='font-semibold text-gray-900'>{selectedOrg?.label || '—'}</span>
          </div>
          <div className='flex justify-between py-1'>
            <span className='text-gray-500'>Project</span>
            <span className='font-semibold text-gray-900'>{selectedProject?.label || '—'}</span>
          </div>
          <div className='flex justify-between py-1'>
            <span className='text-gray-500'>Rental Period</span>
            <span className='font-semibold text-gray-900'>
              {formatRentalPeriod(daysNum, hoursNum)}
            </span>
          </div>
        </div>
        <div className='flex justify-end gap-3 mt-6'>
          <Button type='button' variant='secondary' onClick={onClose}>
            Cancel
          </Button>
          <Button type='submit' variant='primary' isLoading={isSubmitting}>
            Assign Device
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AssignDeviceModal;
